import { useId } from 'react';

import { BackLink } from '@/components/BackLink';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { useOpenings } from '../hooks/useOpenings';
import type { JobSummary } from '../types';
import { GetHelpCallout } from './GetHelpCallout';
import { JobCard } from './JobCard';
import { NoJobsScreen } from './NoJobsScreen';

/**
 * The ordered openings list [KJM-1, KJM-3; Figma `06 · Jobs` — 12021:40673 desktop · 40656 iPad · 40640
 * mobile, all three read 2026-08-21].
 *
 * **Rendered in the order received. There is no sort here** [KJM-1 § Invariant]: the backend already applied
 * radius then the recruiter's manual position, and it deliberately does not send the fields it sorted on, so
 * this component could not reproduce the ranking even if someone tried.
 *
 * **The empty result is a different screen, not an empty list.** Two outcomes end here and they are not the
 * same: a pool that resolved to nothing means the backend already enqueued the candidate to the recruiter
 * queue [decision 0095], while an unreachable backend means nobody knows they are waiting. `NoJobsScreen`
 * says which.
 */
export function JobsScreen() {
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const headingId = useId();
  const selectJob = useKioskSession((state) => state.selectJob);
  const goTo = useKioskSession((state) => state.goTo);
  const { loading, list, unreachable } = useOpenings();

  const openJob = (job: JobSummary) => {
    selectJob(job);
    goTo(SCREEN.jobDetail);
  };

  if (loading) {
    /*
     * The same shape as `CheckingScreen`: spinner, one line, `aria-busy` with a polite live region so the
     * wait is announced once rather than being a silent pause for anyone not watching the ring. Not that
     * component itself — it hard-codes the lookup's own sentence, and this wait is a different one.
     */
    return (
      <KioskScreen>
        <KioskCard header={<StatusBadge label={t('badge.kiosk')} />}>
          <div
            aria-busy="true"
            aria-live="polite"
            className="flex w-full flex-col items-center gap-4 py-12"
          >
            <Spinner />
            <p className="text-sm leading-6 text-[var(--muted-foreground)]">{t('jobs.loading')}</p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * `enqueued` is the claim "a recruiter already has you", so it is asserted from the evidence for it —
   * `handoffReason`, which decision 0095 attaches to a pool that resolved to nothing and which the backend
   * sends only once the candidate is in the queue. It used to be inferred from "the body parsed at all",
   * and that was load-bearing by accident: `radiusMiles` was a required field, so a degenerate `{orders:[]}`
   * failed to parse and fell to the honest screen. Removing that field for decision 0158 took the accident
   * with it, and telling a stranger at an unattended kiosk that a person is coming when nobody was queued
   * leaves them waiting for one. A missing reason now fails to the neutral hand-off instead — as does a
   * BLANK one, which is not evidence of anything: the label is never shown to the candidate, so the only
   * thing it is here for is to say the enqueue happened.
   *
   * `!unreachable` is redundant today — `useOpenings` never stores a list when the request failed — and it
   * stays anyway. That is a cross-module invariant rather than a local one, and a later "keep the stale
   * list across a failed refetch" would quietly make this the same bug again.
   */
  if (unreachable || list === undefined || list.orders.length === 0) {
    return <NoJobsScreen enqueued={!unreachable && Boolean(list?.handoffReason)} />;
  }

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.findJob} />}
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        {/*
          Two blocks 24px apart, each with its own internal rhythm — the frame's `Heading` group is 60px tall
          (28 title + 8 + 24 subtitle) and the cards start 24px below it. An earlier version put all three
          children in one `gap-2` column with a `mt-4` on the list, which rendered a 28px heading block and
          reported as drift on every element beneath it.
        */}
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              id={headingId}
              ref={heading}
              tabIndex={-1}
            >
              {t('jobs.heading')}
            </h1>
            {/*
              One sentence for every candidate, and it names neither a ZIP nor a distance order [ISS-141].
              The list is ranked by the recruiter's manual position alone [KJM-1; decision 0158], so
              "closest first" was false, and the ZIP it named was the first in-radius order's WORKSITE ZIP,
              not the candidate's. `inRadius` stays on the payload, measured and echoed only [decision 0363];
              nothing on this screen reads it.
            */}
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('jobs.subheading')}
            </p>
          </div>

          {/*
            A list, because it is one — `<ul>` tells a screen reader how many openings there are before the
            candidate has tapped through them. The frame's 12px gap between cards is the `gap-3` here; the
            24px between the heading block and the cards is the `mt-4` on the list rather than a gap on the
            column, because the heading's own two lines are 8px apart.
          */}
          {/*
            `aria-labelledby` on the heading rather than a duplicate `aria-label`: the list IS the openings
            the heading names, and it also makes the list addressable — the journey rail above is a list too,
            so "the list of openings" has to be distinguishable from "the list of steps".
          */}
          <ul aria-labelledby={headingId} className="flex w-full flex-col gap-3">
            {list.orders.map((job) => (
              <li className="w-full" key={job.jobOrderId}>
                <JobCard job={job} onOpen={openJob} />
              </li>
            ))}
          </ul>

          {/*
            The frame closes the list with its `Get help` callout, 24px below the last card — the same
            `gap-6` as the heading block above. It was missing entirely, which is what the fidelity gate
            reported at all three widths.
          */}
          <GetHelpCallout />
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
